import { useMemo } from "react";
import { numberToLocaleString } from "@/lib/utils";
import { PlayerDropsMessage } from "./PlayerDropsGenericSubcards";
import { Package2 } from "lucide-react";

type DisplayResourceDatum = {
    label: string;
    count: number;
}

type DrilldownResourcesSubcardProps = {
    resKicks: [string, number][];
};

export default function DrilldownResourcesSubcard({ resKicks }: DrilldownResourcesSubcardProps) {
    let { totalKicks, resources } = useMemo(() => {
        let totalKicks = 0;
        const resources: Record<string, DisplayResourceDatum> = {};
        for (const [resName, cnt] of resKicks) {
            totalKicks += cnt;
            resources[resName] = {
                label: resName,
                count: cnt,
            };
        }
        return {
            totalKicks,
            resources: Object.entries(resources).sort((a, b) => b[1].count - a[1].count),
        };
    }, [resKicks]);

    if (!resources.length) {
        return <PlayerDropsMessage message="No players kicked by resources within this time window." />;
    }

    // Use a single accent color for the resource kicks (blue)
    const accentColor = '#406FE6';
    const topCount = resources[0]?.[1].count ?? 1;

    return (
        <div className="flex flex-col gap-1.5 w-full">
            {/* Header row */}
            <div className="grid grid-cols-[auto_1fr_auto_auto] gap-3 px-3 py-1.5 text-[9px] font-mono font-bold tracking-widest text-white/25 uppercase border-b border-white/5">
                <span className="w-6 text-center">#</span>
                <span>Resource</span>
                <span className="text-right min-w-[5ch]">Kicks</span>
                <span className="text-right min-w-[5ch]">Share</span>
            </div>

            {/* Resource rows */}
            {resources.map(([resName, resData], index) => {
                const pct = totalKicks > 0 ? (resData.count / totalKicks) * 100 : 0;
                const barWidthPct = topCount > 0 ? (resData.count / topCount) * 100 : 0;
                const isTop = index === 0;

                return (
                    <div
                        key={resName}
                        className="group grid grid-cols-[auto_1fr_auto_auto] gap-3 items-center px-3 py-2 rounded-lg border border-transparent hover:border-white/8 hover:bg-white/[0.03] transition-all duration-200"
                    >
                        {/* Rank */}
                        <div className="w-6 text-center">
                            {isTop ? (
                                <span
                                    className="text-[10px] font-mono font-black"
                                    style={{ color: accentColor, textShadow: `0 0 8px ${accentColor}80` }}
                                >
                                    #1
                                </span>
                            ) : (
                                <span className="text-[10px] font-mono text-white/25">
                                    #{index + 1}
                                </span>
                            )}
                        </div>

                        {/* Resource name + bar */}
                        <div className="flex flex-col gap-1 min-w-0">
                            <div className="flex items-center gap-2">
                                <Package2
                                    className="size-3 flex-shrink-0 text-white/30 group-hover:text-white/50 transition-colors duration-200"
                                />
                                <span className="font-mono text-xs font-semibold text-slate-200 truncate tracking-wide">
                                    {resData.label}
                                </span>
                            </div>
                            {/* Progress bar */}
                            <div className="w-full h-[3px] bg-white/5 rounded-full overflow-hidden">
                                <div
                                    className="h-full rounded-full transition-all duration-500"
                                    style={{
                                        width: `${barWidthPct}%`,
                                        backgroundColor: accentColor,
                                        boxShadow: isTop ? `0 0 6px ${accentColor}60` : undefined,
                                    }}
                                />
                            </div>
                        </div>

                        {/* Kick count */}
                        <div className="text-right min-w-[5ch]">
                            <span
                                className="font-mono text-sm font-bold"
                                style={isTop ? { color: accentColor } : { color: 'rgba(255,255,255,0.85)' }}
                            >
                                {numberToLocaleString(resData.count)}
                            </span>
                        </div>

                        {/* Share percentage */}
                        <div className="text-right min-w-[5ch]">
                            <span className="font-mono text-xs text-white/40">
                                {numberToLocaleString(pct, 1)}%
                            </span>
                        </div>
                    </div>
                );
            })}

            {/* Footer summary */}
            <div className="mt-1 pt-2 border-t border-white/5 flex items-center justify-between px-3">
                <span className="text-[10px] font-mono text-white/25 uppercase tracking-widest">
                    {resources.length} Resource{resources.length !== 1 ? 's' : ''}
                </span>
                <span className="text-[10px] font-mono font-bold text-white/40">
                    {numberToLocaleString(totalKicks)} total kicks
                </span>
            </div>
        </div>
    );
}
